import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  getLiverampMarketplaceSegments,
  requestLiverampMarketplaceSegments,
} from '~/src/dash/api/liveramp-marketplace';
import {
  ILiverampMarketplaceSegmentsListInfo,
  LivermpMarketplaceAPIKey,
} from '~/src/dash/api/liveramp-segments/liveramp_models';
import { getFilterParams, IFiltersState } from '~/src/dash/components/filters/common';
import { FilterRow } from '~/src/dash/components/filters/FilterRow';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractVisibleColumns,
  IListViewTableColumnInfo,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelector } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelector';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  Panels,
  Row,
  Rows,
  spacing,
  spacingV2,
  Text,
  TextInput,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  navigate,
  RouteAudienceManagerSettingsList,
  RouteSegmentsLiverampDistribution,
} from '~/src/dash/router/routes';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
} from '~/src/dash/utils/loading-states';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  formatLiverampMarketplaceSegmentsResponseIntoLVTRows,
  LiverampDataMarketplaceConfig,
} from '~/src/dash/views/segments/audience/liveramp/marketplace/config';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { TopBar } from '~/src/dash/views/topbar/TopBar';

const BULK_ACTION_COLOR = publicaColors.blue;

export const LiverampMarketplaceSegmentsListView = () => {
  const { session, user } = useBaseStore();
  const isSuperUser = hasRoleSuperUser(user.user);
  const cfg = LiverampDataMarketplaceConfig;
  const { cypressClassName, defaultState, table } = cfg;

  // pagination state
  const [order, setOrder] = useState<Order>(defaultState.order || 'asc');
  const [orderBy, setOrderBy] = useState<string>(defaultState.orderBy || '');
  const [page, setPage] = useState(defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultState.rowsPerPage || 10);

  const [responseData, setResponseData] = useState<ILiverampMarketplaceSegmentsListInfo[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const rows = formatLiverampMarketplaceSegmentsResponseIntoLVTRows(responseData, cfg.table);
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [selected, setSelected] = useState<string[]>([]);
  const isSamsung = session.publisher.ID === '394';
  const [query, setQuery] = useState('');
  const [activeColumnsList, _] = useState<IMenuList>(
    extractVisibleColumns(cfg.table.columns, defaultState.activeColumnKeys || [], isSuperUser),
  );
  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    defaultState.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.table.columns),
  );
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  const [isTableActionPending, setIsTableActionPending] = useState(false);
  const [filters, setFilters] = useState<IFiltersState>({});

  const ManageDistributionButton = () => {
    return (
      <ButtonV2
        clearBackground
        onClick={() => {
          track('LiveRamp Marketplace - Access distribution table', { isSuperUser }, user.user);
          navigate(RouteSegmentsLiverampDistribution, extractRouteParams({ session, user }));
          return;
        }}
        style={{ padding: '5px 16px' }}
      >
        <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
          <Col paddingTop='6px'>
            <IconV2 name='distribution' color={publicaColors.blue} />
          </Col>
          <Col>
            <Text small bold color={publicaColors.blue}>
              Manage Distribution
            </Text>
          </Col>
        </Cols>
      </ButtonV2>
    );
  };

  useEffect(() => {
    getLiverampMarketplaceQuery();
  }, [page, rowsPerPage, query, filters, orderBy, order]);

  const getLiverampMarketplaceQuery = () => {
    setIsTableActionPending(true);
    setLoadState(LoadingStateLoading);
    const filterParams = getFilterParams(filters, cfg.filters);
    getLiverampMarketplaceSegments(session.publisher.ID, {
      urlQueries: {
        pageSize: rowsPerPage,
        page,
        search: query,
        orderBy,
        orderDirection: order,
        ...filterParams,
      },
    })
      .then(([marketplaceData, err]) => {
        if (err !== undefined) {
          setSnackbarMessage('Error fetching Segments.');
          setSnackbarError(true);
          setIsTableActionPending(false);
          // handle error
        }
        const data: ILiverampMarketplaceSegmentsListInfo[] =
          (marketplaceData && marketplaceData.liveramp_marketplace_segments) || [];

        if (marketplaceData) {
          setResponseData(data);
          setTotalRowCount(marketplaceData.total_count || 0);
          setIsTableActionPending(false);
          setLoadState(LoadingStateHasData);
        }
        return;
      })
      .catch(() => {
        setIsTableActionPending(false);
        return;
      });
  };

  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;

    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setPage(0);
    });
  };

  const handleOnChangeSelect = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    unstable_batchedUpdates(() => {
      setSelected([]);
      setPage(newPage);
    });
  };

  const handleOnChangeQuery = (text: string) => {
    track('LiveRamp Marketplace - Search', { isSuperUser }, user.user);
    setQuery(text);
  };

  // active columns
  const handleChangeActiveColumns = (keys: string[]) => {
    track(
      'LiveRamp Marketplace - Active Cols',
      { isSuperUser, activeColumns: keys.join(',') },
      user.user,
    );
    setActiveColumnKeys(keys);
  };

  const handleAcceptActiveColumns = (keys: string[]) => {
    setActiveColumnKeys(keys);
    defaultState.activeColumnKeys = keys as LivermpMarketplaceAPIKey[];
  };

  const handleRequestSegments = () => {
    track('LiveRamp Marketplace - Request Segments', { isSuperUser }, user.user);
    setIsTableActionPending(true);
    requestLiverampMarketplaceSegments(
      {
        segment_ids: selected,
      },
      session.publisher.ID,
    )
      .then(([_, err]) => {
        if (err !== undefined) {
          setSnackbarMessage('Error requesting Segments.');
          setSnackbarError(true);
          setIsTableActionPending(false);
          return;
        }
        setSnackbarMessage(
          `Successfully requested Segments. Activate segments in Manage Distribution.`,
        );
        setSnackbarError(false);
        setIsTableActionPending(false);
        setSelected([]);
        getLiverampMarketplaceQuery();
      })
      .catch(() => {
        setSnackbarMessage('Error requesting Segments.');
        setSnackbarError(true);
        setIsTableActionPending(false);
        return;
      });
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  // filter functions
  const handleFilterAccept = (menuTitle: string, list: IMenuList) => {
    track('LiveRamp Marketplace - Use Filters', { isSuperUser }, user.user);
    const key = menuTitle;
    if (!filters[key] && isEmpty(list)) {
      cfg.filters[key].defaultSelected = [];
      return;
    }
    setFilters((preFilters: any) => {
      const clonedFilters = cloneDeep(preFilters);
      clonedFilters[key] = {
        key,
        list,
      };
      cfg.filters[key].defaultSelected = Object.values(list).map((item) => item.ID);
      return clonedFilters;
    });
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(rows.map((n: any) => n.ID.value));
    } else {
      setSelected([]);
    }
  };

  return (
    <Panels max vertical relative minWidthMain>
      <Panels
        max
        vertical
        relative
        smallVerticalPadding
        largeHorizontalPadding
        style={{ rowGap: spacing.medium }}
      >
        <Panel style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
          <Rows>
            <Row noPadding>
              <TopBar title='Audience Manager' enableUINotices />
            </Row>
            <Row noHorizontalPadding smallVerticalPadding>
              <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <Col>
                  <Text extraLarge bold disableSelect inlineBlock>
                    LiveRamp Data Marketplace
                  </Text>
                </Col>
                <Col>
                  <Cols style={{ alignItems: 'center' }} gap={spacing.small}>
                    <Col>
                      <ManageDistributionButton />
                    </Col>
                    <Col>
                      <ButtonV2
                        clearBackground
                        onClick={() => {
                          navigate(
                            RouteAudienceManagerSettingsList,
                            extractRouteParams({ session, user }),
                          );
                          return;
                        }}
                        style={{ padding: '5px 16px 5px 12px' }}
                      >
                        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                          <Col displayFlex style={{ alignItems: 'center' }}>
                            <IconV2 name='close' small color={publicaColors.blue} />
                          </Col>
                          <Col>
                            <Text sizeV2 small bold color={publicaColors.blue}>
                              Close
                            </Text>
                          </Col>
                        </Cols>
                      </ButtonV2>
                    </Col>
                  </Cols>
                </Col>
              </Cols>
            </Row>
          </Rows>
        </Panel>
        <Panel>
          <Text small baseWeight>
            View 'Requested' and 'Available to Request' segments. Easily search, select, and add
            segments to your distribution, then activate them on the Manage Distribution page.
          </Text>
        </Panel>
        <Panel highlighted>
          <Rows>
            <Row noPadding>
              <FilterRow
                cypressParentName={cypressClassName}
                filtersConfig={cfg.filters}
                handleFilterAccept={handleFilterAccept}
                handleClearFilters={() => {
                  setFilters({});
                }}
                enableClear={true}
              />
            </Row>
          </Rows>
        </Panel>
        <Panel highlighted largeHorizontalPadding verticalPadding>
          <Rows>
            <Row style={{ padding: '8px 0px' }}>
              <Cols style={{ justifyContent: 'space-between' }}>
                <Col>
                  <Cols
                    wrap
                    gap={spacing.medium}
                    style={{
                      alignItems: 'center',
                      rowGap: spacing.small,
                    }}
                  >
                    <Col>
                      <Cols
                        wrap
                        gap={spacing.medium}
                        style={{
                          alignItems: 'center',
                          rowGap: spacing.small,
                        }}
                      >
                        <Col>
                          <TextInput
                            enableClear
                            handleOnChange={handleOnChangeQuery}
                            inputStyle={
                              isSamsung ? { backgroundColor: publicaColors.veryLightGray } : {}
                            }
                            msDelay={500}
                            placeholder='Search by name'
                            prefix='search'
                          />
                        </Col>
                        <Col>
                          <SearchResultsText
                            numRowsDisplayed={responseData.length}
                            numTotalRows={totalRowCount}
                          />
                        </Col>
                      </Cols>
                    </Col>
                  </Cols>
                </Col>
                <Col>
                  <Cols
                    wrap
                    gap={spacing.extraSmall}
                    style={{
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      rowGap: spacing.extraSmall,
                    }}
                  >
                    <Col>
                      <ColumnSelector
                        // simple multi select props
                        preselectedColumnKeys={cfg.defaultState.activeColumnKeys}
                        resetActiveColumnKeys={cfg.columnSelector.resetKeys}
                        columnsList={activeColumnsList}
                        columnsHandleOnChangeOption={handleChangeActiveColumns}
                        columnsHandleOnAccept={handleAcceptActiveColumns}
                      />
                    </Col>
                  </Cols>
                </Col>
              </Cols>
            </Row>
            <Row style={{ padding: '4px 4px 8px' }}>
              {selected.length > 0 && (
                <Cols
                  wrap
                  gap={spacing.small}
                  style={{
                    alignItems: 'center',
                    rowGap: spacing.extraSmall,
                  }}
                >
                  <Col>
                    <Text extraSmall muted>
                      {selected.length} Segments selected
                    </Text>
                  </Col>
                  <Col>
                    <ButtonV2
                      clearBackground
                      allCaps
                      onClick={handleRequestSegments}
                      style={{ padding: '5px 16px 5px 12px' }}
                    >
                      <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                        <Col paddingTop='6px'></Col>
                        <Col>
                          <Text small bold color={BULK_ACTION_COLOR}>
                            + REQUEST SEGMENTS
                          </Text>
                        </Col>
                      </Cols>
                    </ButtonV2>
                  </Col>
                </Cols>
              )}
            </Row>
            <Row noPadding marginTop={spacing.large}>
              <ListViewTable
                isWriteAuthorized={true}
                isReadAuthorized={true}
                className=''
                loadState={loadState}
                activeColumnKeys={activeColumnKeys}
                columns={table.columns}
                order={order}
                orderBy={orderBy}
                page={page}
                rows={rows}
                rowsPerPage={rowsPerPage}
                totalRowCount={totalRowCount}
                handleOnClickColumnHeader={handleOnClickColumnHeader}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                selected={selected}
                handleOnChangeSelect={handleOnChangeSelect}
                enableSelectCheckbox
                isActionPending={isTableActionPending}
                handleSelectAllClick={handleSelectAllClick}
              />
            </Row>
          </Rows>
        </Panel>
        <SnackbarView
          open={!!snackbarMessage}
          error={!!snackbarError}
          onClose={handleSnackbarClose}
        >
          {snackbarMessage}
        </SnackbarView>
      </Panels>
    </Panels>
  );
};
